import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from './config/api';
import { LogOut, ChevronRight } from 'lucide-react';
import PinEntry, { PinSubmitResult } from './PinEntry';
import { retryAfterSeconds } from '../utils/retryAfter';
import LoginLogoMark from './LoginLogoSculpture';
import { SUPPORT_PHONE_DISPLAY } from './SupportContact';
import './Login.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  role: string;
  role_display: string;
  pin_length: number;
  has_pin: boolean;
}

interface StoreInfo {
  id: number;
  store_name: string;
  address?: string;
  phone_number?: string;
  email?: string;
}

/** 429 χωρίς αναγνώσιμο Retry-After (π.χ. API σε άλλο origin): τόση αναμονή πριν από νέα προσπάθεια. */
const PIN_RETRY_FALLBACK_SECONDS = 60;

interface EmployeeSelectionProps {
  storeToken: string;
  store: StoreInfo;
  onEmployeeLogin: (data: any) => void;
  onLogout: () => void;
}

const EmployeeSelection: React.FC<EmployeeSelectionProps> = ({
  storeToken,
  store,
  onEmployeeLogin,
  onLogout
}) => {
  const { t } = useTranslation('auth');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinLoading, setPinLoading] = useState(false);
  // 429: ως πότε ο server δεν δέχεται PIN. Το όριο είναι ανά IP (όλο το κατάστημα), άρα μένει και αν
  // πατήσεις «Πίσω» και διαλέξεις άλλον υπάλληλο.
  const [pinRetryAt, setPinRetryAt] = useState<number | null>(null);
  // Μία αίτηση τη φορά· «Πίσω» ακυρώνει ό,τι εκκρεμεί (η αργοπορημένη απάντηση αγνοείται).
  const pinInFlightRef = useRef(false);
  const pinAttemptRef = useRef(0);

  useEffect(() => {
    fetchEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.id, storeToken]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/auth/store/${store.id}/employees`, {
        headers: {
          'Authorization': `Bearer ${storeToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Αποτυχία φόρτωσης υπαλλήλων — δοκίμασε ξανά.');
      }

      const data = await response.json();
      // Ο διανομέας δεν συνδέεται (απόφαση ιδιοκτήτη 12/10/2026 — routes/auth.js ROLE_CANNOT_LOGIN).
      setEmployees((data.employees || []).filter((emp: Employee) => emp.role !== 'driver'));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // No-PIN employees are unclickable (the button is disabled), so selection
  // only ever happens for employees that can actually log in.
  const handleEmployeeSelect = (employee: Employee) => {
    setSelectedEmployee(employee);
    setPinError(null);
  };

  // Μία αίτηση ανά PIN: το PinEntry στέλνει μία φορά ανά πληκτρολόγημα και εδώ κόβεται κάθε δεύτερη
  // όσο εκκρεμεί η πρώτη (24/10/2026 — πριν, κάθε λάθος PIN έφευγε 2 φορές και έτρωγε διπλό όριο).
  const handlePinSubmit = async (pin: string): Promise<PinSubmitResult | void> => {
    if (!selectedEmployee || pinInFlightRef.current) return;
    if (pinRetryAt !== null && pinRetryAt > Date.now()) return 'rate_limited';

    const attempt = ++pinAttemptRef.current;
    const stillCurrent = () => attempt === pinAttemptRef.current;
    pinInFlightRef.current = true;
    setPinLoading(true);
    setPinError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/pin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storeToken}`
        },
        body: JSON.stringify({
          employee_id: selectedEmployee.id,
          pin
        })
      });

      // Το 429 του express-rate-limit έχει σώμα { error } (χωρίς message)· άλλο σώμα (π.χ. HTML) δεν είναι λόγος για «αποτυχία».
      const data = await response.json().catch(() => ({}));
      if (!stillCurrent()) return;

      if (response.status === 429) {
        // Όριο προσπαθειών (pinLimiter / authLimiter): ΔΕΝ είναι λάθος PIN — πες πόσο να περιμένει.
        const seconds = retryAfterSeconds(response.headers, data) ?? PIN_RETRY_FALLBACK_SECONDS;
        setPinRetryAt(Date.now() + seconds * 1000);
        return 'rate_limited';
      }

      if (!response.ok) {
        // Το backend απαντά 'Invalid PIN' στα αγγλικά — δείξε πάντα το ελληνικό.
        setPinError(/invalid pin|pin not set/i.test(String(data.message || '')) || !data.message ? t('wrongPin') : data.message);
        return 'rejected';
      }

      // Successful login
      onEmployeeLogin(data);
      return 'ok';
    } catch (err: any) {
      if (!stillCurrent()) return;
      setPinError(t('loginFailed'));
      return 'rejected';
    } finally {
      if (stillCurrent()) {
        pinInFlightRef.current = false;
        setPinLoading(false);
      }
    }
  };

  const handlePinCancel = () => {
    // Ό,τι εκκρεμεί δεν αφορά πια την οθόνη: η απάντησή του αγνοείται.
    pinAttemptRef.current += 1;
    pinInFlightRef.current = false;
    setPinLoading(false);
    setSelectedEmployee(null);
    setPinError(null);
  };

  // Show PIN entry if employee is selected
  if (selectedEmployee) {
    return (
      <PinEntry
        employee={selectedEmployee}
        onSubmit={handlePinSubmit}
        onCancel={handlePinCancel}
        error={pinError}
        loading={pinLoading}
        retryAt={pinRetryAt}
        storeName={store.store_name}
      />
    );
  }

  const today = new Date().toLocaleDateString('el-GR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Νέα εμφάνιση (14/10/2026): ίδια μπάρα/υποσέλιδο με τη σύνδεση και το PIN, το κατάστημα ως τίτλος,
  // υπάλληλοι ως ήσυχα πλακίδια (όνομα, επώνυμο, ρόλος ως κείμενο — όχι χρωματιστά σήματα ανά ρόλο).
  // Κινητό: κάθετη λίστα (Login.css, < 600px). Η λογική από πάνω έμεινε ίδια.
  return (
    <div className="xl-page">
      <header className="xl-bar">
        <span className="xl-brand">
          <LoginLogoMark />
          <span>Xentrix Flow</span>
        </span>
        <button type="button" onClick={onLogout} className="xl-ghost" aria-label={t('logout')}>
          <LogOut aria-hidden="true" />
          <span className="xl-hide-narrow">{t('logout')}</span>
        </button>
      </header>

      <main className="xl-main xl-staff-main">
        <div className="xl-staff-wrap">
          <div className="xl-staff-head">
            <p className="xl-eyebrow">{today}</p>
            <h1 className="xl-title">{store.store_name}</h1>
            <p className="xl-sub">{t('pin.pickHint')}</p>
          </div>

          {loading ? (
            <div className="xl-state" role="status">
              <span className="xl-spinner" aria-hidden="true" />
              <p>{t('common:loading', { ns: 'common' })}</p>
            </div>
          ) : error ? (
            <div className="xl-state">
              <p className="xl-state-error">{error}</p>
              <button type="button" onClick={fetchEmployees} className="xl-ghost">
                {t('common:tryAgain', { ns: 'common' })}
              </button>
              {/* Δεν συνδέεται — δείξε αμέσως το τηλέφωνο υποστήριξης */}
              <p className="xl-support-card">
                <span>{t('pin.connectionProblem')}</span>
                <strong>{SUPPORT_PHONE_DISPLAY}</strong>
              </p>
            </div>
          ) : employees.length === 0 ? (
            <div className="xl-state">
              <p>{t('common:noResults', { ns: 'common' })}</p>
            </div>
          ) : (
            <>
              {pinError && !selectedEmployee && (
                <div className="xl-error xl-staff-pin-error" role="alert">
                  <span>{pinError}</span>
                </div>
              )}

              <section className="xl-staff-grid" aria-label={t('selectEmployee')}>
                {employees.map((employee) => (
                  <button
                    key={employee.id}
                    type="button"
                    onClick={() => handleEmployeeSelect(employee)}
                    className="xl-staff"
                    disabled={!employee.has_pin}
                  >
                    <span className="xl-avatar">
                      {employee.avatar_url ? (
                        <img src={employee.avatar_url} alt={employee.first_name} />
                      ) : (
                        <span aria-hidden="true">{employee.first_name.charAt(0)}{employee.last_name.charAt(0)}</span>
                      )}
                    </span>
                    <span className="xl-staff-text">
                      {/* Πλακίδιο: όνομα και επώνυμο σε δύο γραμμές· κινητό: σε μία */}
                      <span className="xl-staff-name">
                        <span className="xl-staff-first">{employee.first_name}</span>{' '}
                        <span className="xl-staff-last">{employee.last_name}</span>
                      </span>
                      <span className="xl-staff-role">{employee.role_display}</span>
                      {!employee.has_pin && <span className="xl-staff-nopin">{t('pin.noPin')}</span>}
                    </span>
                    <ChevronRight className="xl-staff-chevron" aria-hidden="true" />
                  </button>
                ))}
              </section>
            </>
          )}
        </div>
      </main>

      <footer className="xl-foot">
        <span className="xl-support xl-foot-end">{t('pin.support')}<strong>{SUPPORT_PHONE_DISPLAY}</strong></span>
      </footer>
    </div>
  );
};

export default EmployeeSelection;
